/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import type * as Types from '../../../__generated__/zeus/types';

import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
/** Party entering the buyer-agent offer in the portal. */
export type ListingOfferActor =
  /** The buyer agent is entering their own offer. */
  | 'BUYER_AGENT'
  /** A listing partner is entering an offer supplied by the buyer agent. */
  | 'LISTING_PARTNER';

/** Whether the submitting buyer agent belongs to an MLS. Decides which agent identifiers the offer must carry, and how the buyer agent is credited on the sale. */
export type ListingOfferAgentType =
  /** The submitting agent belongs to a multiple listing service. */
  | 'MLS_AGENT'
  /** The submitting agent does not belong to a multiple listing service. */
  | 'NON_MLS_AGENT';

/** Status of the buyer’s home when the offer depends on selling it. */
export type ListingOfferBuyerHomeSaleStatus =
  /** The buyer’s home is already under contract. */
  | 'IN_CONTRACT'
  /** The buyer’s home is listed for sale. */
  | 'LISTED'
  /** The buyer’s home is not listed for sale. */
  | 'UNLISTED';

/** Reference to a document uploaded through agentOfferDocumentUploadUrlCreate. */
export type ListingOfferDocumentInput = {
  /** Opaque handle returned for an agent-offer document upload; it must belong to the listing being offered. */
  documentId: Types.Scalars['ID']['input'];
};

/** Financing method proposed for the offer. */
export type ListingOfferFinancingType =
  /** The offer is funded without a mortgage. */
  | 'CASH'
  /** The offer uses a conventional mortgage. */
  | 'CONVENTIONAL'
  /** The buyer has not determined the financing method. */
  | 'DONT_KNOW'
  /** The offer uses an FHA-insured mortgage. */
  | 'FHA_LOAN'
  /** The offer uses the Texas-specific VA loan option. */
  | 'TX_VA_LOAN'
  /** The offer uses a USDA-backed mortgage. */
  | 'USDA_LOAN'
  /** The offer uses a VA-backed mortgage. */
  | 'VA_LOAN';

/** Reason an offer value failed strict intake validation. */
export type ListingOfferIntakeViolationReason =
  /** A field is required because another offer choice activates it. */
  | 'CONDITIONALLY_REQUIRED'
  /** A submitted value duplicates another value where uniqueness is required. */
  | 'DUPLICATE'
  /** A required offer value was not provided. */
  | 'MISSING'
  /** A submitted value is outside the permitted range or choices. */
  | 'OUT_OF_BOUNDS'
  /** A placeholder value was submitted instead of a real answer. */
  | 'PLACEHOLDER';

/** Lender relationship selected for a financed offer. */
export type ListingOfferLenderType =
  /** The buyer plans to use a lender that is not an Opendoor partner. */
  | 'NON_PARTNER'
  /** The buyer plans to use Opendoor Home Loans. */
  | 'OPENDOOR_HOME_LOANS'
  /** The buyer plans to use an Opendoor trusted lender. */
  | 'TRUSTED_LENDER';

/** Who the buyer agent chooses to coordinate signatures for this offer. */
export type ResaleSignatureCoordination =
  /** The buyer agent will coordinate signatures and return the signed papers. */
  | 'BUYER_AGENT'
  /** Requests that Opendoor coordinate the required signatures. */
  | 'OPENDOOR';

/** Buyer-agent offer terms and contact details submitted for strict intake. */
export type SubmitListingOfferInput = {
  /** Who is submitting the offer: the buyer agent themselves, or a listing partner entering an offer the buyer agent sent outside the portal. Contact fields always identify the buyer agent. */
  actor: ListingOfferActor;
  /** First name of the buyer agent, as they typed it, at most 200 characters. Required by intake, which answers with a typed violation when it is absent. Kept separate from agentLastName because the pair is sent to the MLS to credit the agent on the sale. */
  agentFirstName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Last name of the buyer agent, as they typed it, at most 200 characters. Required by intake, which answers with a typed violation when it is absent. */
  agentLastName?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Real estate license number of the buyer agent, at most 200 characters. Accepted only when agentType is NON_MLS_AGENT and refused otherwise; omit it in the markets that do not ask for one. */
  agentLicenseNumber?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** MLS identifier of the buyer agent, at most 200 characters. Required when agentType is MLS_AGENT and refused otherwise; it is what credits the agent on the sale. */
  agentMlsId?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** MLS office identifier of the buyer agent’s brokerage, at most 200 characters. Accepted only when agentType is MLS_AGENT and refused otherwise; omit it in the markets that do not ask for one. */
  agentMlsOfficeId?: Types.Scalars['NonEmptyString']['input'] | null | undefined;
  /** Whether the buyer agent belongs to an MLS. MLS_AGENT requires agentMlsId and accepts agentMlsOfficeId; NON_MLS_AGENT refuses both and accepts agentLicenseNumber instead. Required by intake, which answers with a typed violation when it is absent. */
  agentType?: ListingOfferAgentType | null | undefined;
  /** Appraisal contingency in days; 0 means explicitly waived. */
  appraisalPeriodDays: Types.Scalars['Int']['input'];
  /** Buyer agent compensation as a fractional rate between 0 and 0.04; 0 means explicitly waived. */
  brokerCommissionRate: Types.Scalars['Float']['input'];
  /** Email address of the buyer; omit when the buyer’s email is unavailable. */
  buyerEmail?: Types.Scalars['EmailAddress']['input'] | null | undefined;
  /** Set only when the offer is contingent on the buyer selling their home. */
  buyerHomeSaleStatus?: ListingOfferBuyerHomeSaleStatus | null | undefined;
  /** Name of the buyer making the offer. */
  buyerName: Types.Scalars['NonEmptyString']['input'];
  /** Requested closing date for the purchase. */
  closeOfEscrow: Types.Scalars['Date']['input'];
  /** Requested seller concessions in cents; 0 means none requested. */
  concessionsFromSellerCents: Types.Scalars['SafeInt']['input'];
  /** Email address of the buyer agent submitting the offer. */
  contactEmail: Types.Scalars['EmailAddress']['input'];
  /** Name of the buyer agent submitting the offer. */
  contactName: Types.Scalars['NonEmptyString']['input'];
  /** Phone number of the buyer agent submitting the offer. */
  contactPhone: Types.Scalars['PhoneNumber']['input'];
  /** Documents supporting the offer, previously uploaded through agentOfferDocumentUploadUrlCreate; at most 10. Every document must belong to the offered listing. */
  documents?: Array<ListingOfferDocumentInput> | null | undefined;
  /** Required for loan financing; must be omitted for non-loan financing (cash or unknown). */
  downPaymentCents?: Types.Scalars['SafeInt']['input'] | null | undefined;
  /** Earnest money deposit in cents; 0 means explicitly waived. */
  earnestDepositCents: Types.Scalars['SafeInt']['input'];
  /** Financing contingency period in days; 0 means explicitly waived, with a maximum of 30 days. */
  financingPeriodDays: Types.Scalars['Int']['input'];
  /** How the buyer plans to finance the purchase. */
  financingType: ListingOfferFinancingType;
  /** Inspection contingency period in days; 0 means explicitly waived, with a maximum of 20 days. */
  inspectionPeriodDays: Types.Scalars['Int']['input'];
  /** Required for loan financing; must be omitted for non-loan financing (cash or unknown). */
  lenderType?: ListingOfferLenderType | null | undefined;
  /** Numeric identifier of the Opendoor-owned listing receiving the offer. */
  listingId: Types.Scalars['ID']['input'];
  /** Offered purchase price in cents; must be greater than zero. */
  priceCents: Types.Scalars['SafeInt']['input'];
  /** Who will coordinate the required signatures. Omit when no choice has been made; omission does not select Opendoor. */
  signatureCoordination?: ResaleSignatureCoordination | null | undefined;
};

export type SubmitAgentListingOfferMutationVariables = Exact<{
  input: Types.SubmitListingOfferInput;
}>;


export type SubmitAgentListingOfferMutation = { submitListingOffer:
    | { __typename: 'ListingOfferIntakeRejection', message: Types.Scalars['String']['output'], violations: Array<{ field: Types.Scalars['String']['output'] | null, reason: Types.ListingOfferIntakeViolationReason, message: Types.Scalars['String']['output'] }> }
    | { __typename: 'ListingOfferSubmitted', offerId: Types.Scalars['ID']['output'] }
   };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}

export const SubmitAgentListingOfferDocument = /*#__PURE__*/ new TypedDocumentString(`
    mutation SubmitAgentListingOffer($input: SubmitListingOfferInput!) {
  submitListingOffer(input: $input) {
    __typename
    ... on ListingOfferSubmitted {
      offerId
    }
    ... on ListingOfferIntakeRejection {
      message
      violations {
        field
        reason
        message
      }
    }
  }
}
    `) as unknown as TypedDocumentString<SubmitAgentListingOfferMutation, SubmitAgentListingOfferMutationVariables>;